import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, ArrowLeft, Users, Package } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { full_name: "", phone: "", email: "", address: "", city: "", notes: "" };

const Field = ({ label, children }) => (
  <div>
    <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [historyClient, setHistoryClient] = useState(null);
  const [historyShipments, setHistoryShipments] = useState(null);

  const openHistory = async (c) => {
    setHistoryClient(c);
    setHistoryShipments(null);
    try {
      const { data } = await api.get(`/clients/${c.id}/shipments`);
      setHistoryShipments(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setHistoryShipments([]);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/clients", { params: q.trim() ? { q: q.trim() } : {} });
    setClients(data);
    setLoading(false);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      full_name: c.full_name, phone: c.phone, email: c.email || "",
      address: c.address, city: c.city || "", notes: c.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      full_name: form.full_name, phone: form.phone, email: form.email || null,
      address: form.address, city: form.city || null, notes: form.notes || null,
    };
    try {
      if (editing) {
        await api.patch(`/clients/${editing.id}`, payload);
        toast.success("Fiche client mise à jour");
      } else {
        await api.post("/clients", payload);
        toast.success("Client créé");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/clients/${toDelete.id}`);
      toast.success("Client supprimé");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-8" data-testid="clients-page">
      <div className="mx-auto max-w-7xl">
        <Button variant="ghost" className="mb-4 gap-2 pl-0 text-sm text-slate-600"
                onClick={() => navigate("/admin")} data-testid="clients-back-button">
          <ArrowLeft className="h-4 w-4" /> Retour au tableau de bord
        </Button>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Back-office</p>
            <h1 className="mt-1 flex items-center gap-2 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
              <Users className="h-6 w-6 text-[#2563EB]" /> Clients
            </h1>
          </div>
          <Button className="gap-2 rounded-full bg-[#2563EB] px-6 font-semibold text-white hover:bg-[#1D4ED8]"
                  onClick={openCreate} data-testid="new-client-button">
            <Plus className="h-4 w-4" /> Nouveau client
          </Button>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9"
                     placeholder="Nom, téléphone, email, ville" data-testid="clients-search-input" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Nom complet</th>
                  <th className="px-4 py-3">Téléphone</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Adresse</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Chargement…</td></tr>
                )}
                {!loading && clients.length === 0 && (
                  <tr data-testid="clients-empty-row">
                    <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                      Aucun client enregistré. Créez votre première fiche client.
                    </td>
                  </tr>
                )}
                {!loading && clients.map((c) => (
                  <tr key={c.id} className="transition-colors hover:bg-blue-50/60"
                      data-testid={`client-row-${c.phone.replace(/\s/g, "")}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{c.full_name}</td>
                    <td className="px-4 py-3 font-mono text-slate-700">{c.phone}</td>
                    <td className="px-4 py-3 text-slate-700">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {c.address}
                      {c.city ? ` · ${c.city}` : ""}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" className="gap-2 border-slate-300"
                                onClick={() => openHistory(c)} data-testid={`history-client-${c.id}`}>
                          <Package className="h-4 w-4" /> Colis
                        </Button>
                        <Button variant="outline" size="sm" className="border-slate-300"
                                onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" disabled={!canDelete}
                                title={canDelete ? "Supprimer" : "Réservé à l'administrateur principal"}
                                className="border-rose-300 text-[#DC2626] hover:bg-rose-50"
                                onClick={() => setToDelete(c)} data-testid={`delete-client-${c.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier la fiche client" : "Nouveau client"}</DialogTitle>
            <DialogDescription>
              Ces informations pourront être réutilisées lors de la création d'une livraison.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="client-form">
            <Field label="Nom complet">
              <Input required value={form.full_name} onChange={set("full_name")} className="h-11"
                     data-testid="client-name-input" />
            </Field>
            <Field label="Téléphone">
              <Input required value={form.phone} onChange={set("phone")} className="h-11"
                     placeholder="+225 07 45 88 21" data-testid="client-phone-input" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={set("email")} className="h-11"
                     placeholder="client@exemple.com" data-testid="client-email-input" />
            </Field>
            <Field label="Adresse">
              <Input required value={form.address} onChange={set("address")} className="h-11"
                     data-testid="client-address-input" />
            </Field>
            <Field label="Ville">
              <Input value={form.city} onChange={set("city")} className="h-11" placeholder="Abidjan"
                     data-testid="client-city-input" />
            </Field>
            <Field label="Notes (optionnel)">
              <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="client-notes-input" />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}
                      data-testid="client-cancel-button">
                Annuler
              </Button>
              <Button type="submit" disabled={saving}
                      className="bg-[#1E3A8A] font-semibold text-white hover:bg-[#172d6d]"
                      data-testid="client-save-button">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyClient} onOpenChange={(o) => !o && setHistoryClient(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Colis de {historyClient?.full_name}</DialogTitle>
            <DialogDescription>
              Toutes les livraisons associées à {historyClient?.phone}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="client-history-list">
            {historyShipments === null && <p className="text-sm text-slate-500">Chargement…</p>}
            {historyShipments?.length === 0 && (
              <p className="text-sm text-slate-500" data-testid="client-history-empty">
                Aucun colis enregistré pour ce client.
              </p>
            )}
            {historyShipments?.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/admin/livraison/${s.id}`)}
                className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors hover:bg-blue-50/60"
                data-testid={`client-history-row-${s.tracking_number}`}
              >
                <div>
                  <p className="font-mono font-bold text-[#1E3A8A]">{s.tracking_number}</p>
                  <p className="text-xs text-slate-500">
                    {s.shipping_type === "aerien" ? "Aérien" : "Maritime"} · {s.weight_kg} kg ·{" "}
                    {s.destination_city}
                  </p>
                </div>
                <StatusBadge status={s.status} />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              La fiche de {toDelete?.full_name} sera supprimée. Les livraisons déjà créées ne sont pas
              affectées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-client">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#DC2626] text-white hover:bg-[#b91c1c]"
                               data-testid="confirm-delete-client">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
